// Encapsulated planet icon assets. To swap the icon set (e.g. Nocturna Wheel SVGs),
// replace only the path data inside each component below — the renderer and chart
// component consume the exported names, so no chart code changes are needed.

import type React from 'react';

const PlanetIcon: React.FC<
  React.SVGProps<SVGSVGElement> & { title: string; paths: React.ReactNode }
> = ({ title, paths, ...props }) => (
  <svg fill="none" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" {...props}>
    <title>{title}</title>
    <g fill="currentColor" stroke="currentColor" strokeLinecap="round" strokeWidth={1.4}>
      {paths}
    </g>
  </svg>
);

const SunIcon: React.FC<React.SVGProps<SVGSVGElement>> = (props) => (
  <PlanetIcon
    title="Sun"
    {...props}
    paths={
      <>
        <circle cx="12" cy="12" fill="currentColor" r="4" stroke="none" />
        <path
          d="M12 2v3M12 19v3M2 12h3M19 12h3M4.9 4.9l2.1 2.1M17 17l2.1 2.1M19.1 4.9L17 7M7 17l-2.1 2.1"
          fill="none"
        />
      </>
    }
  />
);

const MoonIcon: React.FC<React.SVGProps<SVGSVGElement>> = (props) => (
  <PlanetIcon
    title="Moon"
    {...props}
    paths={
      <path d="M19 14.5A8 8 0 1 1 9.5 5a6.5 6.5 0 0 0 9.5 9.5z" fill="currentColor" stroke="none" />
    }
  />
);

const MercuryIcon: React.FC<React.SVGProps<SVGSVGElement>> = (props) => (
  <PlanetIcon
    title="Mercury"
    {...props}
    paths={
      <>
        <path d="M7 5a5 5 0 0 0 9.5 2" fill="none" />
        <circle cx="12" cy="9" fill="none" r="3" />
        <path d="M12 12v8M9 17h6" fill="none" />
      </>
    }
  />
);

const VenusIcon: React.FC<React.SVGProps<SVGSVGElement>> = (props) => (
  <PlanetIcon
    title="Venus"
    {...props}
    paths={
      <>
        <circle cx="12" cy="8" fill="none" r="4.5" />
        <path d="M12 12.5v8M8 17h8" fill="none" />
      </>
    }
  />
);

const MarsIcon: React.FC<React.SVGProps<SVGSVGElement>> = (props) => (
  <PlanetIcon
    title="Mars"
    {...props}
    paths={
      <>
        <circle cx="10" cy="14" fill="none" r="4.5" />
        <path d="M13.5 10.5 19 5M14 5h5v5" fill="none" />
      </>
    }
  />
);

const JupiterIcon: React.FC<React.SVGProps<SVGSVGElement>> = (props) => (
  <PlanetIcon
    title="Jupiter"
    {...props}
    paths={
      <>
        <path d="M8 6a5 5 0 1 0 0 8" fill="none" />
        <path d="M4 8c3-1 6-1 9-1s4 2 4 2M5 11h6M5 14h6" fill="none" />
      </>
    }
  />
);

const SaturnIcon: React.FC<React.SVGProps<SVGSVGElement>> = (props) => (
  <PlanetIcon
    title="Saturn"
    {...props}
    paths={
      <>
        <path d="M9 4a5 5 0 1 0 0 8" fill="none" />
        <path d="M9 6h10M13 6c2 2 4 5 4 8M5 10c1 3 4 6 8 6" fill="none" />
      </>
    }
  />
);

const UranusIcon: React.FC<React.SVGProps<SVGSVGElement>> = (props) => (
  <PlanetIcon
    title="Uranus"
    {...props}
    paths={
      <>
        <circle cx="12" cy="14" fill="none" r="4" />
        <path d="M12 10V3M9 6h6" fill="none" />
      </>
    }
  />
);

const NeptuneIcon: React.FC<React.SVGProps<SVGSVGElement>> = (props) => (
  <PlanetIcon
    title="Neptune"
    {...props}
    paths={
      <>
        <path d="M5 9a4 4 0 0 0 6.5 3A4 4 0 0 0 18 9a4 4 0 0 1-6.5 3A4 4 0 0 1 5 9z" fill="none" />
        <path d="M12 12v7M9 19h6" fill="none" />
      </>
    }
  />
);

const PlutoIcon: React.FC<React.SVGProps<SVGSVGElement>> = (props) => (
  <PlanetIcon
    title="Pluto"
    {...props}
    paths={
      <>
        <circle cx="12" cy="7" fill="none" r="2.5" />
        <path d="M12 9.5v8M9 13.5h6" fill="none" />
        <path d="M9 17.5h6" fill="none" />
      </>
    }
  />
);

export {
  JupiterIcon as Jupiter,
  MarsIcon as Mars,
  MercuryIcon as Mercury,
  MoonIcon as Moon,
  NeptuneIcon as Neptune,
  PlutoIcon as Pluto,
  SaturnIcon as Saturn,
  SunIcon as Sun,
  UranusIcon as Uranus,
  VenusIcon as Venus,
};
